/**
 * STD-013 — single source of truth for the Dashboard's top-level tabs.
 *
 * This module is pure metadata: it lists the tab IDs, their i18n label keys,
 * tier (which row of the tab bar they render into), and behavioral flags.
 * The `DashboardTab` union, `VALID_TAB_IDS`, the tier sets, and the `<Tab>`
 * bar are all derived from `TABS` so a new tab is added in one place.
 *
 * Intentionally NO `component` field: under the locked decision (b), the active
 * tab's content is rendered by a `switch (activeTab)` in TabPanel.tsx that calls
 * each tab component directly with its own per-tab props. A `component` field
 * here would be populated-but-dead. It will be reintroduced if/when lazy loading
 * lands (deferred follow-up), at which point the registry can own the component
 * reference for `React.lazy`.
 */

import type { TranslationKey } from '../../../i18n/useTranslation';

export type TabId =
  | 'overview'
  | 'profile'
  | 'maintenance'
  | 'health'
  | 'files'
  | 'inventory'
  | 'calendar';

// Placeholder until a real role system lands (decision 5).
type Role = string;

export interface TabDef {
  id: TabId;
  labelKey: TranslationKey;
  tier: 1 | 2;
  requiresDrone: boolean;
  // When the first drone is selected, switch away from this tab to `profile`.
  // Distinct from tier/requiresDrone (decision 6): inventory & calendar are
  // tier-1 but stay put. Only overview & files set this true.
  autoSwitchOnDroneSelect?: boolean;
  roles?: Role[];
}

export const TABS: readonly TabDef[] = [
  {
    id: 'overview',
    labelKey: 'tabs.overview',
    tier: 1,
    requiresDrone: false,
    autoSwitchOnDroneSelect: true,
  },
  {
    id: 'files',
    labelKey: 'tabs.files',
    tier: 1,
    requiresDrone: false,
    autoSwitchOnDroneSelect: true,
  },
  { id: 'inventory', labelKey: 'tabs.inventory', tier: 1, requiresDrone: false },
  { id: 'calendar', labelKey: 'tabs.calendar', tier: 1, requiresDrone: false },
  { id: 'profile', labelKey: 'tabs.profile', tier: 2, requiresDrone: true },
  { id: 'maintenance', labelKey: 'tabs.maintenance', tier: 2, requiresDrone: true },
  { id: 'health', labelKey: 'tabs.healthPanel', tier: 2, requiresDrone: true },
];

export const VALID_TAB_IDS: ReadonlySet<TabId> = new Set(TABS.map((t) => t.id));

/** Fleet-wide tabs that don't require (or display) a selected drone. */
export const FIRST_TIER_TAB_IDS: ReadonlySet<TabId> = new Set(
  TABS.filter((t) => t.tier === 1).map((t) => t.id)
);

export const TAB_BY_ID: Record<TabId, TabDef> = Object.fromEntries(
  TABS.map((t) => [t.id, t])
) as Record<TabId, TabDef>;
